import React, { useEffect, useRef } from 'react'
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import CoachName from 'src/legacy/components/CoachName/CoachName'
import { AdvancedCareTeamMember } from '../api/types'
import { roleLabelFor } from '../dashboard/careTeam/roleCategories'
import { advancedColors } from '../theme'
import { AdminPatient, patientName } from './adminCareTeamBatch'

export type CareTeamLookup =
  | { status: 'loading' | 'error' }
  | { status: 'ready'; byUid: Record<string, AdvancedCareTeamMember[]> }

type Props = {
  patients: AdminPatient[]
  careTeams: CareTeamLookup
  selected: Record<string, AdminPatient>
  revealSelection?: number
  ending: boolean
  onToggle: (patient: AdminPatient) => void
  onToggleAll: () => void
  onEnd: (patient: AdminPatient, member: AdvancedCareTeamMember) => void
}

const StandardCareTeam = ({ patient }: { patient: AdminPatient }) => (
  <>
    {[...(patient.careTeam ?? [])]
      .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
      .map((member) => (
        <Box key={member.coachID} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
          <Typography variant="body2">
            <CoachName coachID={member.coachID} renderLabel={(name) => name} />
          </Typography>
          <Typography component="div" variant="caption" sx={{ color: advancedColors.inkMuted }}>
            Coach{member.isPrimary && ' (primary)'}
          </Typography>
        </Box>
      ))}
  </>
)

const AdvancedCareTeam = ({
  patient,
  careTeams,
  ending,
  onEnd,
}: { patient: AdminPatient } & Pick<Props, 'careTeams' | 'ending' | 'onEnd'>) => {
  if (careTeams.status !== 'ready') {
    return <Typography variant="body2">{careTeams.status === 'loading' ? '…' : 'Failed to load'}</Typography>
  }

  const members = careTeams.byUid[patient.uid] ?? []
  const standardCoachIds = (patient.careTeam ?? []).map((member) => member.coachID)

  if (members.length === 0) {
    return <Typography variant="body2">None</Typography>
  }

  return (
    <>
      {members.map((member) => (
        <Box
          key={member.memberId}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, '&:last-child': { mb: 0 } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">
              <CoachName coachID={member.providerUid} renderLabel={(name) => name} />
            </Typography>
            <Typography component="div" variant="caption" sx={{ color: advancedColors.inkMuted }}>
              {roleLabelFor(member.role)}
            </Typography>
          </Box>
          {!standardCoachIds.includes(member.providerUid) && (
            <Tooltip title="Not on the Standard care team">
              <WarningAmberRoundedIcon
                fontSize="small"
                aria-label="Not on the Standard care team"
                sx={{ color: advancedColors.yellowAccent }}
              />
            </Tooltip>
          )}
          <Tooltip title="End this membership">
            <IconButton
              size="small"
              aria-label="End this membership"
              disabled={ending}
              onClick={() => onEnd(patient, member)}
            >
              <DeleteOutlineRoundedIcon fontSize="small" sx={{ color: advancedColors.inkMuted }} />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </>
  )
}

const AdminPatientTable = ({
  patients,
  careTeams,
  selected,
  revealSelection = 0,
  ending,
  onToggle,
  onToggleAll,
  onEnd,
}: Props) => {
  const selectedCount = patients.filter((patient) => selected[patient.uid]).length
  const firstSelectedUid = patients.find((patient) => selected[patient.uid])?.uid
  const firstSelectedInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (revealSelection === 0) {
      return
    }
    const input = firstSelectedInput.current
    input?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    input?.focus({ preventScroll: true })
  }, [revealSelection])

  return (
    <TableContainer sx={{ maxHeight: 420, border: `1px solid ${advancedColors.line}`, borderRadius: 1 }}>
      <Table
        size="small"
        stickyHeader
        sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${advancedColors.line}` } }}
      >
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedCount === patients.length}
                indeterminate={selectedCount > 0 && selectedCount < patients.length}
                onChange={onToggleAll}
                inputProps={{ 'aria-label': 'Select every listed patient' }}
              />
            </TableCell>
            <TableCell>Patient</TableCell>
            <TableCell>Standard care team</TableCell>
            <TableCell>Advanced care team</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.map((patient) => (
            <TableRow key={patient.uid} hover selected={Boolean(selected[patient.uid])}>
              <TableCell padding="checkbox">
                <Checkbox
                  inputRef={patient.uid === firstSelectedUid ? firstSelectedInput : undefined}
                  checked={Boolean(selected[patient.uid])}
                  onChange={() => onToggle(patient)}
                  inputProps={{ 'aria-label': `Select ${patientName(patient)}` }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{patientName(patient)}</Typography>
                <Typography variant="caption" sx={{ color: advancedColors.inkMuted }}>
                  {patient.email}
                </Typography>
              </TableCell>
              <TableCell>
                <StandardCareTeam patient={patient} />
              </TableCell>
              <TableCell>
                <AdvancedCareTeam patient={patient} careTeams={careTeams} ending={ending} onEnd={onEnd} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AdminPatientTable
